import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Copy, Plus, TrendingUp, Users, DollarSign, Clock, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SERVICE_TYPES = ["Hotel & Villa", "Viaje Corporativo", "Experiencia VIP", "Aviación & Yate", "Ser Socio Concierge+"];

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [d, r] = await Promise.all([api.get("/partner/dashboard"), api.get("/partner/referrals")]);
      setDash(d.data);
      setReferrals(r.data.referrals);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(dash?.referral_code || "");
    toast.success("Código de referido copiado");
  };

  const stats = dash?.stats || {};
  const cards = [
    { icon: Users, l: "Referidos Totales", v: stats.total_referrals ?? 0 },
    { icon: TrendingUp, l: "Confirmados", v: stats.confirmed ?? 0 },
    { icon: DollarSign, l: "Comisiones Ganadas", v: `${(stats.total_commission ?? 0).toLocaleString("es-ES")} €` },
    { icon: Clock, l: "Pipeline Pendiente", v: `${(stats.pending_pipeline ?? 0).toLocaleString("es-ES")} €` },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="backdrop-blur-xl bg-black/60 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <span className="font-serif-display text-2xl cursor-pointer" onClick={() => navigate("/")}>
            Sol<span className="text-gold">Club</span>
          </span>
          <div className="flex items-center gap-6">
            <span className="text-white/50 font-light text-sm hidden md:inline">{user?.email}</span>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-2 text-xs tracking-luxe-sm uppercase text-white/70 hover:text-gold transition-colors">
              <LogOut size={16} strokeWidth={1.5} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs tracking-luxe uppercase text-gold mb-4">Portal de Partners</p>
            <h1 className="font-serif-display text-4xl md:text-5xl">Bienvenido, {user?.name?.split(" ")[0]}</h1>
          </div>
          <button data-testid="add-referral-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-3 bg-gold text-black px-7 py-4 text-xs tracking-luxe-sm uppercase hover:bg-[#C5A059] transition-colors">
            <Plus size={16} strokeWidth={1.5} /> Nuevo Referido
          </button>
        </div>

        <div className="border border-white/15 p-8 mb-12 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="referral-code-card">
          <div>
            <p className="text-xs tracking-luxe-sm uppercase text-white/50 mb-2">Su Código de Referido</p>
            <p className="font-serif-display text-3xl text-gold" data-testid="referral-code">{dash?.referral_code || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs tracking-luxe-sm uppercase text-white/50 mb-2">Comisión</p>
            <p className="font-serif-display text-3xl">{((dash?.commission_rate ?? 0) * 100).toFixed(0)}%</p>
          </div>
          <button data-testid="copy-code-btn" onClick={copyCode}
            className="flex items-center gap-2 border border-white/25 px-6 py-3 text-xs tracking-luxe-sm uppercase hover:border-gold hover:text-gold transition-colors">
            <Copy size={16} strokeWidth={1.5} /> Copiar
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {cards.map((c) => (
            <div key={c.l} className="border border-white/10 p-8 hover:border-gold/40 transition-colors" data-testid={`stat-${c.l}`}>
              <c.icon size={24} strokeWidth={1} className="text-gold mb-6" />
              <p className="font-serif-display text-3xl md:text-4xl mb-2">{c.v}</p>
              <p className="text-white/50 font-light text-xs tracking-luxe-sm uppercase">{c.l}</p>
            </div>
          ))}
        </div>

        <h3 className="font-serif-display text-2xl mb-6">Sus Referidos</h3>
        <div className="border border-white/10" data-testid="referrals-table">
          <div className="hidden md:grid grid-cols-5 gap-4 px-8 py-4 border-b border-white/10 text-xs tracking-luxe-sm uppercase text-white/40">
            <span>Cliente</span><span>Servicio</span><span>Valor Est.</span><span>Comisión</span><span>Estado</span>
          </div>
          {referrals.length ? referrals.map((r) => (
            <div key={r.id} className="grid grid-cols-2 md:grid-cols-5 gap-4 px-8 py-5 border-b border-white/10 last:border-0 text-sm font-light" data-testid={`referral-row-${r.id}`}>
              <span className="text-white/80">{r.client_name}</span>
              <span className="text-white/60">{r.service_type}</span>
              <span className="text-white/60">{r.estimated_value?.toLocaleString("es-ES")} €</span>
              <span className="text-gold">{r.commission?.toLocaleString("es-ES")} €</span>
              <span className={`text-xs tracking-luxe-sm uppercase ${r.status === "confirmed" ? "text-gold" : "text-white/50"}`}>
                {r.status === "confirmed" ? "confirmado" : "pendiente"}
              </span>
            </div>
          )) : (
            <p className="px-8 py-8 text-white/40 font-light text-sm">Aún no hay referidos. Presente a su primer cliente para empezar a ganar.</p>
          )}
        </div>
      </div>

      {open && <ReferralModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ReferralModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", service_type: SERVICE_TYPES[0], estimated_value: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/partner/referrals", {
        ...form,
        estimated_value: parseFloat(form.estimated_value) || 0,
      });
      toast.success("Referido registrado");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 font-light focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6" data-testid="referral-modal">
      <div className="bg-[#0d0d0d] border border-white/15 w-full max-w-lg p-10">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-serif-display text-2xl">Nuevo Referido</h3>
          <button onClick={onClose} data-testid="close-modal" className="text-white/50 hover:text-white"><X strokeWidth={1.5} /></button>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="referral-form">
          <input required data-testid="ref-client-name" className={field} placeholder="Nombre del cliente"
            value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
          <input type="email" data-testid="ref-client-email" className={field} placeholder="Email del cliente (opcional)"
            value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
          <select data-testid="ref-service" className={`${field} [&>option]:bg-[#121212]`}
            value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="number" data-testid="ref-value" className={field} placeholder="Valor estimado del viaje (EUR)"
            value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
          <textarea rows={2} data-testid="ref-notes" className={field} placeholder="Notas (opcional)"
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <button type="submit" disabled={saving} data-testid="ref-submit"
            className="w-full bg-gold text-black py-4 text-xs tracking-luxe uppercase hover:bg-[#C5A059] transition-colors disabled:opacity-50">
            {saving ? "Registrando…" : "Registrar Referido"}
          </button>
        </form>
      </div>
    </div>
  );
}
